import EventEmitter from "common/utils/EventEmitter";
import PeerConnection from "./PeerConnection";
import { CONNECTION_STATES, USER_ROLE, MEDIA_TYPE } from "./enums/LiveStreamingEnums";
import LiveStreaming from "./LiveStreaming";
import VideoStateManager from "./VideoStateManager";
import LoggerService from "./LoggerService";
import { RequestManager } from "./RequestManager";

// this class is responsible for peer connections hash map
export default class PeerConnectionManager {
  static peerConnectionMap = {};

  static listenToPeerConnectionEvents() {
    EventEmitter.on("UPDATE_CONNECTION_STATE", PeerConnectionManager.connectionStateChangeHandler);
  }

  static connectionStateChangeHandler(data) {
    let { peerId, state } = data;

    if (
      LiveStreaming.config.userRole == USER_ROLE.BROADCASTER &&
      [CONNECTION_STATES.Failed, CONNECTION_STATES.Disconnected, CONNECTION_STATES.Closed].includes(state)
    ) {
      PeerConnectionManager.removePeerConnection(peerId);
    }
  }

  static addPeerConnection(config, requestedStreams,iceServersConfig) {
    PeerConnectionManager.peerConnectionMap[config.peerId] = new PeerConnection(
      config,
      requestedStreams,
      iceServersConfig
    );

    return PeerConnectionManager.peerConnectionMap[config.peerId];
  }

  static getPeerConnection(peerId) {
    return PeerConnectionManager.peerConnectionMap[peerId];
  }

  // 1. close the connection with id = peerId
  // 2. remove the connection from hashMap
  static removePeerConnection(peerId) {
    if (peerId) {
      let peerConnection = PeerConnectionManager.peerConnectionMap[peerId];
      if (peerConnection) {
        delete PeerConnectionManager.peerConnectionMap[peerId];
        peerConnection.stopConnection();
        
        LoggerService.info(
          LoggerService.MODULE.STREAMING,
          `[Streaming] [PeerConnectionManager] [peerId: ${peerId}] [CONNECTION DESTROYED]`
        );
      }
    }
  }

  // 1. check if a connection already exists with id == peerId
  // 2. if connection exists, close and remove the connection
  // 3. create a new connection with id = peerId
  static async startPeerConnection(config, listeners, requestedStreams = {}, iceServersConfig = null) {
    try {
    let peerConnection = PeerConnectionManager.getPeerConnection(config.peerId);

    if (peerConnection) {
      PeerConnectionManager.removePeerConnection(config.peerId);
    }
     
    // notify offer received 
    if (config.userRole == USER_ROLE.VIEWER) {
      RequestManager.notifyOfferReceived(config);  
    }

    peerConnection = PeerConnectionManager.addPeerConnection(config, requestedStreams,iceServersConfig);
    await peerConnection.createConnection(listeners);

    LoggerService.info(
      LoggerService.MODULE.STREAMING,
      `[Streaming] [peerId: ${config.peerId}] [PeerConnectionManager] [${requestedStreams.webcam ? "WEBCAM" : "SCREEN"}] [CONNECTION CREATED]`
    );
      
    } catch (error) {
      LoggerService.error(
        LoggerService.MODULE.STREAMING,
        `[Streaming] [peerId: ${config.peerId}] [PeerConnectionManager] [${requestedStreams.webcam ? "WEBCAM" : "SCREEN"}] [FAILED TO CREATE CONNECTION]`,
        error
      );
    }
  }

  static getLiveStreamingMapping() {
    const originalMap = PeerConnectionManager.peerConnectionMap;
    const map = {};

    for (const peerId in originalMap) {
      map[peerId] = {};

      for (const key in originalMap[peerId]) {
        const value = originalMap[peerId][key];

        if (typeof value !== "function") {
          map[peerId][key] = value;
        }
      }
    }

    return map;
  }

  static async stopPeerConnection(peerId) {
    let peerConnection = PeerConnectionManager.getPeerConnection(peerId);

    if (peerConnection) {
      await peerConnection.stopConnection();
    }
  }

  static addMediaStream(peerId, stream) {
    let peerConnection = PeerConnectionManager.getPeerConnection(peerId);
    if (peerConnection) {
      peerConnection.addTrack(stream);
    }
  }

  static removeMediaStream(peerId) {
    let peerConnection = PeerConnectionManager.getPeerConnection(peerId);
    if (peerConnection) {
      peerConnection.removeTrack();
    }
  }

  static getRemoteStream(peerId) {
    let peerConnection = PeerConnectionManager.getPeerConnection(peerId);
    if (peerConnection) {
      return peerConnection.getStream();
    }
    return null;
  }

  static getConnectionState(peerId) {
    let peerConnection = PeerConnectionManager.getPeerConnection(peerId);
    if (peerConnection) {
      return peerConnection.getConnectionState();
    }
    return null;
  }

  static getSignalingState(peerId) {
    let peerConnection = PeerConnectionManager.getPeerConnection(peerId);
    if (peerConnection) {
      return peerConnection.getSignalingState();
    }
    return null;
  }

  static getIceConnectionState(peerId) {
    let peerConnection = PeerConnectionManager.getPeerConnection(peerId);
    if (peerConnection) {
      return peerConnection.getIceConnectionState();
    }
    return null;
  }

  static async close() {
    for (const peerId in PeerConnectionManager.peerConnectionMap) {
      PeerConnectionManager.removePeerConnection(peerId);
    }
  }

  static async checkBeforeClosing() {
    for (const peerId in PeerConnectionManager.peerConnectionMap) {
      const connectionState = PeerConnectionManager.getConnectionState(peerId);

      if (connectionState != CONNECTION_STATES.Connected) {
        PeerConnectionManager.removePeerConnection(peerId);
      }
    }
  }

  static getMediaType(requestedStreams) {
    if (requestedStreams.webcam) {
      return MEDIA_TYPE.WEBCAM;
    }

    if (requestedStreams.screen) {
      return MEDIA_TYPE.SCREENSHARE;
    }
  }

  static notifyActiveConnections() {
    for (const peerId in PeerConnectionManager.peerConnectionMap) {
      const peerConnection = PeerConnectionManager.getPeerConnection(peerId);
      
      if (!peerConnection) {
        continue;
      }

      const connectionState = peerConnection.connectionState;

      if (connectionState == CONNECTION_STATES.Connected) {

        const mediaType = PeerConnectionManager.getMediaType(peerConnection.requestedStreams);
        const videoId = VideoStateManager.getVideoId(peerConnection.peerId, mediaType);

        VideoStateManager.updateConnectionStatus(peerConnection.roomId, peerConnection.peerId, videoId, mediaType, connectionState);
      }
    }
  }
}
